<script setup>
    import SpeakersHero from '~/components/blocks/two-six/speakers/hero.vue';
    import CompactSessionCard from '~/components/blocks/two-six/sessions/compact-session-card.vue';

    const props = defineProps({
        speaker: {
            type: Object,
            required: true,
        },
        cardsTheme: {
            type: String,
            default: 'light',
            validator: (v) => ['light', 'dark'].includes(v),
        },
        currentYear: {
            type: Number,
            default: 2026,
        },
    });

    // Per-viewer state (on my schedule, seat held, clashing). These sessions
    // arrive nested in the speaker payload rather than from the sessions feed,
    // but the same rule applies: none of it is in that response, so the cards
    // rendered plain however the session actually stood for the viewer (Nick,
    // Asana 1217708959827809 and 1217708959827824).
    //
    // Applied to the CURRENT year only — the Previous Sessions list is past
    // events, which cannot be scheduled, seated or in conflict.
    const { applyViewerState } = useViewerSessionState();

    const allSessions = computed(() => Array.isArray(props.speaker?.sessions) ? props.speaker.sessions : []);
    const currentSessions = computed(() => applyViewerState(allSessions.value.filter(s => s.year === props.currentYear)));
    const previousSessions = computed(() => allSessions.value.filter(s => s.year !== props.currentYear));
</script>

<template>
    <div class="speaker-detail flex flex-col gap-[60px] md:gap-[80px] text-[var(--magenta)]">
        <SpeakersHero :speaker="speaker" :key="speaker.id" />

        <section v-if="currentSessions.length"
            class="flex flex-col gap-[40px] md:gap-[50px]">
            <h3 class="font-hubspot-serif font-normal m-0 text-[30px] md:text-[48px] leading-[1.2]">
                Speaker Sessions:
            </h3>
            <ul class="flex flex-col gap-[20px] list-none p-0 m-0">
                <li v-for="session in currentSessions" :key="session.id">
                    <!-- show-add-button: these cards had no way to add a session,
                         unlike every other session card on the site (UNB). Only on
                         current-year sessions — the Previous Sessions list below is
                         past events, which can't be scheduled. -->
                    <CompactSessionCard :session="session"
                        hideFeatured
                        :speaker-size="70"
                        :theme="cardsTheme"
                        :clamp-description="false"
                        use-full-description
                        show-add-button
                        show-speaker-names />
                </li>
            </ul>
        </section>

        <section v-if="previousSessions.length"
            class="flex flex-col gap-[40px] md:gap-[50px]">
            <h3 class="font-hubspot-serif font-normal m-0 text-[30px] md:text-[48px] leading-[1.2]">
                Previous Sessions:
            </h3>
            <ul class="flex flex-col gap-[20px] list-none p-0 m-0">
                <li v-for="session in previousSessions" :key="session.id">
                    <CompactSessionCard :session="session"
                        hideFeatured
                        :speaker-size="70"
                        :theme="cardsTheme"
                        :clamp-description="false"
                        use-full-description
                        show-speaker-names />
                </li>
            </ul>
        </section>
    </div>
</template>
